import React, { useState, useEffect } from "react";
import { Disclosure, Transition } from "@headlessui/react";
import { useNavigate, useLocation } from "react-router-dom";
import CaseDetailsSectionLatestMobile from "../case-tracking/CaseDetailsSectionLatestMobile";

import { makeBulkPayment } from "../../services/Common";
import SharedModal from "../happiness/SharedModal";
import SuccessPayment from "../paymentPopup/SuccessPayment";

const InvoicesPaymentSection = ({
  service,
  arPhrase,
  enPhrase,
  lang,
  requests,
  servicesList = [],
  selectedRequests = [],
  getRequestUniqueKey,
  caseid,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [paymentMethod, setPaymentMethod] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isMobileExpanded, setIsMobileExpanded] = useState(true);

  const selectedCount = selectedRequests.length;

  const calculatedTotals = servicesList.reduce(
    (acc, item, index) => {
      const itemKey = getRequestUniqueKey
        ? getRequestUniqueKey(item, index)
        : index;

      if (selectedRequests.includes(itemKey)) {
        const vat = Number(item?.vatValue || 0);
        const excludingVat = Number(item?.excludingVAT || 0);

        acc.totalExcludingVAT += excludingVat;
        acc.totalVatValue += vat;
        acc.totalServiceValue += excludingVat + vat;
      }

      return acc;
    },
    {
      totalServiceValue: 0,
      totalExcludingVAT: 0,
      totalVatValue: 0,
    }
  );

  const localSelectedServiceIds = servicesList
    .filter((item, index) => {
      const itemKey = getRequestUniqueKey
        ? getRequestUniqueKey(item, index)
        : index;

      return selectedRequests.includes(itemKey);
    })
    .map((item) => item.id)
    .filter(Boolean);

  const cachedCallbackServiceId =
    typeof window !== "undefined"
      ? window.sessionStorage.getItem("temp_success_service_id")
      : "";

  const activeServiceIdsModalProp = cachedCallbackServiceId || "";

  const normalizePaymentUrlCallback = (paymentUrl, fallbackInvoiceId) => {
    try {
      if (!paymentUrl) return paymentUrl;

      const callbackMatch = paymentUrl.match(/([?&])Callback=/i);

      if (!callbackMatch || callbackMatch.index === undefined) {
        return paymentUrl;
      }

      const separator = callbackMatch[1];
      const callbackKey = "Callback=";

      const callbackStartIndex = callbackMatch.index;
      const callbackValueStartIndex =
        callbackStartIndex + separator.length + callbackKey.length;

      const paymentUrlBeforeCallback = paymentUrl.substring(
        0,
        callbackStartIndex
      );

      const rawCallbackValue = paymentUrl.substring(callbackValueStartIndex);

      const paymentMainUrl = new URL(paymentUrlBeforeCallback);

      const servicesValue =
        paymentMainUrl.searchParams.get("Services") ||
        paymentMainUrl.searchParams.get("services");

      const firstServiceId = servicesValue
        ? servicesValue.split("-")[0]
        : localSelectedServiceIds?.[0];

      const decodedCallbackValue = decodeURIComponent(rawCallbackValue);
      const oldCallbackUrl = new URL(decodedCallbackValue);

      const invoiceId =
        oldCallbackUrl.searchParams.get("invoiceId") ||
        oldCallbackUrl.searchParams.get("InvoiceId") ||
        fallbackInvoiceId;

      const serviceId =
        oldCallbackUrl.searchParams.get("serviceId") ||
        oldCallbackUrl.searchParams.get("ServiceId") ||
        firstServiceId;

      if (!invoiceId) {
        console.error("Missing invoiceId while normalizing callback URL");
        return paymentUrl;
      }

      if (!serviceId) {
        console.error("Missing serviceId while normalizing callback URL");
        return paymentUrl;
      }

      const fixedCallbackUrl = `${window.location.origin}/invoice-details/${invoiceId}?serviceId=${serviceId}`;

      const normalizedPaymentUrl = `${paymentUrlBeforeCallback}${separator}${callbackKey}${encodeURIComponent(
        fixedCallbackUrl
      )}`;

      console.log("Normalized payment URL before redirect:", {
        originalPaymentUrl: paymentUrl,
        normalizedPaymentUrl,
        fixedCallbackUrl,
        invoiceId,
        serviceId,
      });

      return normalizedPaymentUrl;
    } catch (error) {
      console.error("Failed to normalize payment callback URL:", error, {
        paymentUrl,
      });

      return paymentUrl;
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const pathInvoiceMatch = location.pathname.match(
      /\/invoice-details\/([^/?]+)/
    );

    let statusCode =
      searchParams.get("StatusCode") ||
      searchParams.get("statusCode") ||
      searchParams.get("statuscode");

    let incomingInvoiceId =
      searchParams.get("invoiceId") ||
      searchParams.get("InvoiceId") ||
      searchParams.get("invoiceID") ||
      pathInvoiceMatch?.[1];

    let incomingServiceId =
      searchParams.get("serviceId") ||
      searchParams.get("ServiceId") ||
      searchParams.get("serviceID");

    const callbackUrl =
      searchParams.get("Callback") ||
      searchParams.get("callback") ||
      searchParams.get("ReturnUrl") ||
      searchParams.get("returnUrl");

    if (callbackUrl) {
      try {
        const decodedCallbackUrl = decodeURIComponent(callbackUrl);
        const parsedCallback = new URL(decodedCallbackUrl);
        const callbackParams = new URLSearchParams(parsedCallback.search);

        incomingInvoiceId =
          incomingInvoiceId ||
          callbackParams.get("invoiceId") ||
          callbackParams.get("InvoiceId") ||
          callbackParams.get("invoiceID");

        incomingServiceId =
          incomingServiceId ||
          callbackParams.get("serviceId") ||
          callbackParams.get("ServiceId") ||
          callbackParams.get("serviceID");

        statusCode =
          statusCode ||
          callbackParams.get("StatusCode") ||
          callbackParams.get("statusCode") ||
          callbackParams.get("statuscode");
      } catch (error) {
        console.error("Invalid callback URL:", callbackUrl, error);
      }
    }

    console.log("Payment callback values:", {
      fullUrl: window.location.href,
      locationPathname: location.pathname,
      locationSearch: location.search,
      callbackUrl,
      statusCode,
      incomingInvoiceId,
      incomingServiceId,
    });

    const hasPaymentCallback =
      statusCode ||
      incomingServiceId ||
      callbackUrl ||
      searchParams.get("PPId") ||
      searchParams.get("ppId") ||
      searchParams.get("PPID");

    if (!hasPaymentCallback) {
      return;
    }

    if (incomingServiceId) {
      window.sessionStorage.setItem(
        "temp_success_service_id",
        incomingServiceId
      );

      console.log("Stored callback serviceId:", incomingServiceId);
    }

    if (incomingInvoiceId) {
      window.sessionStorage.setItem(
        "temp_success_invoice_id",
        incomingInvoiceId
      );
    }

    const cachedInvoiceId = window.sessionStorage.getItem(
      "temp_success_invoice_id"
    );

    if (incomingInvoiceId && location.pathname === "/invoice-details") {
      navigate(`/invoice-details/${incomingInvoiceId}${location.search}`, {
        replace: true,
      });
      return;
    }

    if (statusCode === "100" || (incomingInvoiceId && incomingServiceId)) {
      setShowSuccessPopup(true);

      window.history.replaceState(
        {},
        document.title,
        `/invoice-details/${incomingInvoiceId || cachedInvoiceId || caseid}`
      );
    }
  }, [location.search, location.pathname, navigate, caseid]);

  useEffect(() => {
    if (selectedCount > 1 && paymentMethod === "bank") {
      setPaymentMethod("digital");
    }
  }, [selectedCount, paymentMethod]);

  const handlePaymentSubmit = async (e) => {
    if (e) {
      e.stopPropagation();
    }

    if (selectedCount === 0 || isSubmitting) {
      return;
    }

    if (paymentMethod === "bank" && selectedCount === 1) {
      const selectedItem = servicesList.find((item, index) => {
        const itemKey = getRequestUniqueKey
          ? getRequestUniqueKey(item, index)
          : index;

        return selectedRequests.includes(itemKey);
      });

      if (selectedItem) {
        navigate(`/payment-Via-Bank/${selectedItem.caseId}/${selectedItem.id}`);
      }

      return;
    }

    if (paymentMethod === "digital") {
      setIsSubmitting(true);

      try {
        console.log(
          "Collected Service IDs for Payment API:",
          localSelectedServiceIds
        );

        if (!localSelectedServiceIds.length) {
          console.error("No selected service IDs found before payment");
          return;
        }

        window.sessionStorage.removeItem("temp_success_service_id");
        window.sessionStorage.setItem("temp_success_invoice_id", caseid);

        const response = await makeBulkPayment(localSelectedServiceIds, caseid);

        if (!response) {
          console.error("Payment processing failed: empty response");
          return;
        }

        console.log("Payment initialization payload successful:", response);

        let paymentUrl = response?.paymentUrl || response?.data?.paymentUrl;

        if (!paymentUrl) {
          console.error("Payment URL not found", response);
          return;
        }

        if (paymentUrl.includes("{0}") || paymentUrl.includes("%7B0%7D")) {
          paymentUrl = paymentUrl
            .replaceAll("{0}", caseid)
            .replaceAll("%7B0%7D", caseid);
        }

        paymentUrl = normalizePaymentUrlCallback(paymentUrl, caseid);

        window.location.href = paymentUrl;
      } catch (error) {
        console.error(
          "Error executing dynamic batch transaction sequence:",
          error
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="flex flex-col w-full md:w-[30%] justify-start gap-[16px] mb-[12px] items-center max-md:pb-[100px]">
      <Disclosure
        as="div"
        className="w-full bg-[#F3F0EA] border-[0.5px] border-[#E6D7A2] shadow-[0px_10px_40px_#361E1214] rounded-[8px] flex flex-col items-center gap-[24px] max-md:hidden"
        defaultOpen={false}
      >
        {({ open }) => (
          <div className="w-full">
            <Disclosure.Button className="group flex w-full items-center justify-between px-[16px] h-[56px]">
              <p className="font-Almarai font-bold text-[14px] leading-[20px] text-[#361E12]">
                {lang === "en"
                  ? enPhrase["lblCaseDetails"]
                  : arPhrase["lblCaseDetails"]}
              </p>

              <div className="flex items-center justify-center gap-2">
                <img
                  src="/assets/img/add-request/dash.svg"
                  alt="dash"
                  className="w-[1px] h-[20px]"
                />
                <img
                  className={`size-5 transition-transform duration-500 ease-in-out ${
                    open ? "rotate-0" : "rotate-180"
                  }`}
                  src="/assets/img/add-request/ArrowUP.svg"
                  alt="arrow"
                />
              </div>
            </Disclosure.Button>

            <Transition
              show={open}
              enter="transition-all duration-700 ease-in-out"
              enterFrom="max-h-0 opacity-0"
              enterTo="opacity-100"
              leave="transition-all duration-500 ease-in-out"
              leaveFrom="opacity-100"
              leaveTo="max-h-0 opacity-0"
              className="overflow-hidden flex items-center justify-center"
            >
              <Disclosure.Panel
                static
                className={`${
                  typeof window !== "undefined" && window.innerWidth <= 768
                    ? "mx-[4px] mb-[4px]"
                    : "mx-[8px] mb-[8px]"
                } w-[99%] rounded-[12px] border-[0.5px] border-solid border-[#D4D2CE] bg-[#fff] shadow-[0px_10px_40px_#351F131F] p-[16px] gap-[16px] flex flex-col transition-all duration-500 ease-in-out overflow-visible`}
              >
                <CaseDetailsSectionLatestMobile
                  service={requests?.case}
                  lang={lang}
                  arPhrase={arPhrase}
                  enPhrase={enPhrase}
                />
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>

      <div
        onClick={() => {
          if (window.innerWidth < 768) {
            setIsMobileExpanded(!isMobileExpanded);
          }
        }}
        className="w-full flex flex-col border !border-[#E6D7A2] bg-[#F3F0EA]  gap-[8px] transition-all duration-300 ease-in-out max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-40 max-md:bg-[#292724] max-md:p-4  max-md:shadow-[0px_-8px_30px_rgba(0,0,0,0.15)] md:pb-[8px] md:p-[16px]"
      >
        <div className="flex flex-row justify-between items-center gap-[4px] w-full max-md:order-1">
          <div className="flex flex-col items-start">
            <p className="text-[12px] md:text-[14px] font-normal leading-[18px] md:leading-[24px] text-[#6F6A62] max-md:font-bold max-md:text-[#D4D2CE]">
              {lang === "en"
                ? enPhrase["lblClaimedValueTotal"]
                : arPhrase["lblClaimedValueTotal"]}
            </p>

            {!isMobileExpanded && (
              <div className="text-[10px] text-[#B68A35] flex flex-row items-center gap-[4px] font-semibold md:hidden mt-0.5">
                <img
                  src="/assets/img/invoices/CheckCircle.svg"
                  alt=""
                  className="w-[20px] h-[20px]"
                />
                <p className="text-[#BCB9B3] text-[12px] font-Almarai font-normal">
                  {selectedCount}{" "}
                  {lang === "en"
                    ? enPhrase["lblSelectedRequest"]
                    : arPhrase["lblSelectedRequest"]}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-row gap-[8px] items-center">
            <div
              className={`flex ${
                lang === "en" ? "flex-row-reverse" : ""
              } gap-[3px] items-center justify-center`}
            >
              <p className="text-[20px] md:text-[18px] text-[#B68A35] font-Almarai font-bold">
                {calculatedTotals.totalServiceValue}
              </p>

              <span className="text-muted text-[14px] md:small text-decoration-line-through !text-[#B68A35] flex items-center h-full">
                <span
                  className="uae-symbol !text-[20px] md:!text-[18px] !text-[#B68A35] leading-[18px] md:font-bold"
                  lang="en"
                  dir="ltr"
                >
                  &#x00EA;
                </span>
              </span>
            </div>

            <img
              src="/assets/img/invoices/CaretDown.svg"
              alt=""
              className={`w-[20px] h-[20px] md:hidden rotate-180 ${
                isMobileExpanded ? "hidden" : ""
              }`}
            />
          </div>
        </div>

        <div className="border !border-[#B68A3540] my-1 max-md:hidden" />

        <div
          className={`flex flex-col gap-[8px] overflow-hidden transition-all duration-300 ${
            typeof window !== "undefined" &&
            window.innerWidth < 768 &&
            !isMobileExpanded
              ? "max-md:max-h-0 max-md:opacity-0"
              : "max-h-[500px] opacity-100"
          }`}
        >
          <div className="flex gap-[3px] items-center justify-between md:hidden">
            <p className="text-[14px] text-[#D4D2CE] font-Almarai font-bold">
              {lang === "en"
                ? enPhrase["lblSummaryReuest"]
                : arPhrase["lblSummaryReuest"]}
            </p>

            <img
              src="/assets/img/invoices/CaretDown.svg"
              alt=""
              className="w-[20px] h-[20px]"
            />
          </div>

          <div className="w-full flex flex-row justify-between gap-[4px] max-md:pt-2">
            <p className="text-[12px] hidden md:block md:text-[14px] font-normal leading-[18px] md:leading-[24px] text-[#737373]">
              {lang === "en"
                ? enPhrase["lblOriginalAmount"]
                : arPhrase["lblOriginalAmount"]}
            </p>

            <div className="text-[10px] text-[#B68A35] flex flex-row items-center gap-[4px] font-semibold md:hidden mt-0.5">
              <img
                src="/assets/img/invoices/CheckCircle.svg"
                alt=""
                className="w-[20px] h-[20px]"
              />
              <p className="text-[#BCB9B3] text-[12px] font-Almarai font-normal">
                {selectedCount}{" "}
                {lang === "en"
                  ? enPhrase["lblSelectedRequest"]
                  : arPhrase["lblSelectedRequest"]}
              </p>
            </div>

            <div
              className={`flex ${
                lang === "en" ? "flex-row-reverse" : ""
              } gap-[3px] items-center justify-center`}
            >
              <p className="text-[12px] md:text-[14px] text-[#737373] font-Almarai font-bold">
                {calculatedTotals.totalExcludingVAT}
              </p>

              <span className="text-muted text-[12px] md:small text-decoration-line-through !text-[#737373] flex items-center h-full">
                <span
                  className="uae-symbol !text-[12px] md:!text-[14px] !text-[#737373] leading-[18px] md:font-bold"
                  lang="en"
                  dir="ltr"
                >
                  &#x00EA;
                </span>
              </span>
            </div>
          </div>

          <div className="w-full flex flex-row justify-between gap-[4px]">
            <p className="text-[12px] md:text-[14px] font-normal leading-[18px] md:leading-[24px] text-[#737373]">
              {lang === "en"
                ? enPhrase["lblAmountVat"]
                : arPhrase["lblAmountVat"]}
            </p>

            <div
              className={`flex ${
                lang === "en" ? "flex-row-reverse" : ""
              } gap-[3px] items-center justify-center`}
            >
              <p className="text-[12px] md:text-[14px] text-[#737373] font-Almarai font-bold">
                {calculatedTotals.totalVatValue}
              </p>

              <span className="text-muted text-[12px] md:small text-decoration-line-through !text-[#737373] flex items-center h-full">
                <span
                  className="uae-symbol !text-[12px] md:!text-[14px] !text-[#737373] leading-[18px] md:font-bold"
                  lang="en"
                  dir="ltr"
                >
                  &#x00EA;
                </span>
              </span>
            </div>
          </div>

          <div className="border !border-[#B68A3540] my-1" />

          <div className="hidden md:flex flex-row items-center justify-center text-[12px] md:text-[12px] font-bold leading-[18px] md:leading-[24px] text-[#6F6A62] gap-[4px]">
            <span>{selectedCount}</span>
            <p>
              {lang === "en"
                ? enPhrase["lblSelectedRequest"]
                : arPhrase["lblSelectedRequest"]}
            </p>
          </div>
        </div>

        <button
          onClick={handlePaymentSubmit}
          disabled={selectedCount === 0 || !paymentMethod || isSubmitting}
          className={`w-full rounded-[8px] h-[48px] transition-colors flex items-center justify-center p-[8px] mt-2 md:hidden max-md:order-2 ${
            selectedCount === 0 || !paymentMethod || isSubmitting
              ? "bg-[#D4D2CE] cursor-not-allowed text-[#FFFFFF]"
              : "bg-[#B68A35] hover:bg-[#a37a2f] text-white font-bold"
          }`}
        >
          <span className="text-[14px] font-Almarai font-bold text-[#FFFFFF]">
            {isSubmitting
              ? lang === "en"
                ? enPhrase["lblProcessingPay"]
                : arPhrase["lblProcessingPay"]
              : lang === "en"
                ? enPhrase["btnPayNow"]
                : arPhrase["btnPayNow"]}
          </span>
        </button>
      </div>

      <div className="w-full flex flex-col gap-[8px] max-md:px-0 max-md:w-full mt-2">
        <p className="text-[12px] md:text-[16px] font-bold leading-[18px] md:leading-[24px] text-[#010202]">
          {lang === "en"
            ? enPhrase["lblChosePaymentMethod"]
            : arPhrase["lblChosePaymentMethod"]}
        </p>

        {selectedCount <= 1 && (
          <div className="flex flex-row gap-[8px] justify-between p-[16px] border-[0.5px] border-[#D4D2CE] bg-[#FFFFFF] rounded-[8px]">
            <div className="flex flex-col gap-[8px]">
              <div className="flex flex-row items-center gap-[8px]">
                <img
                  src="/assets/img/invoices/bank.svg"
                  alt=""
                  className="w-[20px] h-[20px]"
                />
                <p className="text-[12px] md:text-[14px] font-bold leading-[18px] md:leading-[20px] text-[#292724]">
                  {lang === "en"
                    ? enPhrase["lblPaymentBank"]
                    : arPhrase["lblPaymentBank"]}
                </p>
              </div>

              <p className="text-[12px] md:text-[14px] font-normal leading-[18px] md:leading-[24px] text-[#666666]">
                {lang === "en"
                  ? enPhrase["lblPaymentBankDetails"]
                  : arPhrase["lblPaymentBankDetails"]}
              </p>
            </div>

            <input
              type="radio"
              name="paymentMethod"
              checked={paymentMethod === "bank"}
              onChange={() => setPaymentMethod("bank")}
              className="w-[20px] h-[20px] rounded-[20px] text-[#af8c45] focus:ring-[#af8c45] cursor-pointer"
            />
          </div>
        )}

        <div className="flex flex-row gap-[8px] justify-between p-[16px] border-[0.5px] border-[#D4D2CE] bg-[#FFFFFF] rounded-[8px]">
          <div className="flex flex-col gap-[8px]">
            <div className="flex flex-row items-center gap-[8px]">
              <img
                src="/assets/img/invoices/Mastercard.svg"
                alt=""
                className="w-[20px] h-[20px]"
              />
              <p className="text-[12px] md:text-[14px] font-bold leading-[18px] md:leading-[20px] text-[#292724]">
                {lang === "en"
                  ? enPhrase["lblPaymentElectronic"]
                  : arPhrase["lblPaymentElectronic"]}
              </p>
            </div>

            <p className="text-[12px] md:text-[14px] font-normal leading-[18px] md:leading-[24px] text-[#666666]">
              {lang === "en"
                ? enPhrase["lblPaymentElectronicDetails"]
                : arPhrase["lblPaymentElectronicDetails"]}
            </p>
          </div>

          <input
            type="radio"
            name="paymentMethod"
            checked={paymentMethod === "digital"}
            onChange={() => setPaymentMethod("digital")}
            className="w-[20px] h-[20px] rounded-[20px] text-[#af8c45] focus:ring-[#af8c45] cursor-pointer"
          />
        </div>

        <p className="text-[12px] md:text-[12px] font-normal leading-[14px] md:leading-[16px] text-[#B68A35]">
          {lang === "en"
            ? enPhrase["lblNoteElectronicPay"]
            : arPhrase["lblNoteElectronicPay"]}
        </p>

        <button
          onClick={handlePaymentSubmit}
          disabled={selectedCount === 0 || !paymentMethod || isSubmitting}
          className={`w-full rounded-[4px] h-[48px] transition-colors flex items-center justify-center p-[8px] max-md:hidden ${
            selectedCount === 0 || !paymentMethod || isSubmitting
              ? "bg-[#D4D2CE] cursor-not-allowed text-[#FFFFFF]"
              : "bg-[#B68A35] hover:bg-[#a37a2f] text-white font-bold"
          }`}
        >
          <span className="text-[14px] font-Almarai md:text-[16px] font-bold text-[#FFFFFF]">
            {isSubmitting
              ? lang === "en"
                ? enPhrase["lblProcessingPay"]
                : arPhrase["lblProcessingPay"]
              : lang === "en"
                ? enPhrase["btnPayNow"]
                : arPhrase["btnPayNow"]}
          </span>
        </button>
      </div>

      {showSuccessPopup && (
        <SharedModal isSuccessInvoice setOpenModal={setShowSuccessPopup}>
          <SuccessPayment
            setOpenModal={setShowSuccessPopup}
            lang={lang}
            arPhrase={arPhrase}
            enPhrase={enPhrase}
            service={service}
            IsInvoice
            selectedServiceIds={activeServiceIdsModalProp}
          />
        </SharedModal>
      )}
    </div>
  );
};

export default InvoicesPaymentSection;